import React, { createElement, forwardRef, useContext, useEffect, useState } from 'react'
import { VStack, HStack, Text, Box, useDisclosure } from '@chakra-ui/react'
import { SessionToken } from '@mapbox/search-js-core'
import cx from 'classnames'
import { EBreakPoint } from 'enums/theme'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import DatePicker from 'react-datepicker'
import { useFormContext, useWatch } from 'react-hook-form'
import CustomDateInput from 'components/CustomDateInput'
import FormInput from 'components/FormInput'
import FormInputWithMask from 'components/FormInputWithMask'
import { IAddressSuggestion, IApplicationFormValues } from 'interfaces/application'
import { ApplicationContext } from '../../applicationForm.context'
import AutoFillInput from './components/AutoFillInput'
import ChooseBedroom from './components/ChooseBedroom'
import { handleChangeAddress } from './utils'

export interface IAddressOption extends IAddressSuggestion {
  label: string
  value: string
}

interface IShareYourDeetsProps {
  showChooseBedRoom?: boolean
}

const ShareYourDeets = (props: IShareYourDeetsProps) => {
  const { showChooseBedRoom = true } = props
  const { register, setValue, control, reset, getValues } = useFormContext()
  const { houseApplicationStore } = useStores()
  const { isEntireHouse } = useContext(ApplicationContext)
  const moveDate: Date = useWatch({ control, name: 'moveInDate' }) || new Date()
  const isTablet: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.LG)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [defaultOptions, setDefaultOptions] = useState<IAddressOption[]>([])
  const [chosenAddress, setChosenAddress] = useState<IAddressOption>()
  const [address, setAddress] = useState<string>('')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const formData: IApplicationFormValues = getValues()
  const [newSessionToken, setNewSessionToken] = useState<string>('')

  useEffect(() => {
    if (!isEmpty(chosenAddress)) {
      reset({ ...formData, ...omit(chosenAddress, ['id', 'label', 'value', 'fullAddress']) })
    }
  }, [chosenAddress])

  useEffect(() => {
    if (address) {
      handleChangeAddress(
        onClose,
        setIsLoading,
        houseApplicationStore,
        address,
        newSessionToken,
        setDefaultOptions,
        onOpen
      )
    } else {
      setDefaultOptions([])
      setChosenAddress({} as IAddressOption)
      onClose()
    }
  }, [address])

  useEffect(() => {
    const sessionToken = new SessionToken()
    setNewSessionToken(sessionToken?.id)
  }, [])

  return (
    <VStack
      spacing={{ base: 6, lg: 8 }}
      paddingX={4}
      marginTop={{ base: 0, lg: 32 }}
      paddingTop={{ base: 6, lg: 8 }}
      paddingBottom={{ base: 6, md: 14, lg: 4 }}
      maxWidth={{ base: 'full', lg: '530px' }}
      marginBottom={{ base: '65px', lg: 0 }}
      marginX="auto"
      width="full"
    >
      {!isEntireHouse && showChooseBedRoom && <ChooseBedroom />}
      <VStack alignItems="flex-start" width="full">
        <Text fontSize="md" fontWeight={500} color="gray.700" alignSelf={'flex-start'}>
          About when would you want to move in?
        </Text>
        <Box width={{ base: 'fit-content', lg: 'full' }} alignSelf={{ base: 'center', md: 'flex-start' }}>
          <FormInput name="moveInDate" autoComplete="off">
            <DatePicker
              {...register('moveInDate')}
              selected={moveDate}
              minDate={new Date()}
              onChange={(date: Date) => setValue('moveInDate', date)}
              customInput={createElement(forwardRef(CustomDateInput))}
              inline={isTablet}
              showPopperArrow={false}
              calendarClassName={cx({ ['isMobile']: isTablet })}
              calendarStartDay={1}
            />
          </FormInput>
        </Box>
      </VStack>
      {isTablet ? (
        <VStack spacing={2} alignItems="flex-start" width="full">
          <Text fontSize="md" fontWeight="500" color="gray.700">
            Date of Birth
          </Text>
          <HStack spacing={4} alignItems="flex-start" width="full">
            <FormInputWithMask
              name="month"
              label="Month"
              format="##"
              placeholder="MM"
              mask="M"
              isSmallLabel
              autoComplete="off"
              onValueChange={(numberValues) => {
                if (numberValues.value?.length === 2) {
                  const monthInputElement: HTMLElement | null = document.getElementById('day')
                  monthInputElement && monthInputElement.focus()
                }
              }}
            />
            <FormInputWithMask
              name="day"
              id="day"
              label="Day"
              format="##"
              placeholder="DD"
              mask="D"
              isSmallLabel
              autoComplete="off"
              onValueChange={(numberValues) => {
                if (numberValues.value?.length === 2) {
                  const yearInputElement: HTMLElement | null = document.getElementById('year')
                  yearInputElement && yearInputElement.focus()
                }
              }}
            />
            <FormInputWithMask
              name="year"
              id="year"
              label="Year"
              format="####"
              placeholder="YYYY"
              mask="Y"
              isSmallLabel
              autoComplete="off"
            />
          </HStack>
        </VStack>
      ) : (
        <FormInputWithMask
          name="dateOfBirth"
          label="Date of Birth"
          format="##/##/####"
          placeholder="MM/DD/YYYY"
          mask={['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']}
          autoComplete="off"
        />
      )}
      <FormInputWithMask
        name="ssn"
        label="SSN (Required background check)"
        placeholder="e.g. 123-45-6789"
        format="###-##-####"
        mask={'X'}
        autoComplete="off"
      />
      <FormInput
        name="monthlyIncome"
        label="Your current monthly income"
        placeholder="$0.00"
        autoComplete="off"
        isRequired={false}
      />
      <Text fontSize="md" fontWeight={600} color="gray.700" alignSelf={'flex-start'}>
        Most recent permanent address
      </Text>
      <HStack width="full" spacing={0} marginTop="16px !important">
        <AutoFillInput
          isLoading={isLoading}
          defaultOptions={defaultOptions}
          setDefaultOptions={setDefaultOptions}
          setChosenAddress={setChosenAddress}
          setAddress={setAddress}
          isOpenDropdown={isOpen}
          onCloseDropdown={onClose}
        />
      </HStack>
      <FormInput
        name="addressLine2"
        label="Address Line 2"
        placeholder="Enter Address line 2"
        disabled={isLoading}
        isRequired={false}
        autoComplete="off"
      />
      <FormInput name="city" label="City" placeholder="Enter City" disabled={isLoading} autoComplete="off" />
      <FormInput name="state" label="State" placeholder="Enter State" disabled={isLoading} autoComplete="off" />
      <FormInput name="zipCode" label="Zip Code" placeholder="Enter Zip Code" disabled={isLoading} autoComplete="off" />
    </VStack>
  )
}

export default ShareYourDeets
